import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
  writeBatch
} from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../firebase";
import {
  calculateAnnualTaxObligation,
  endOfMonthIso,
  normalizeAnnualTaxRule
} from "../helpers/annualTaxObligation";

const BOARD_ID = "annual-tax-obligations";
const cleanId = (value) => String(value || "").trim().replace(/[^a-zA-Z0-9_-]/g, "-");
const cleanFileName = (value) => String(value || "belge")
  .trim()
  .replace(/[^a-zA-Z0-9._-]/g, "-")
  .replace(/-+/g, "-")
  .slice(-180);

const resolveContentType = (file) => {
  if (file?.type) return file.type;
  const extension = String(file?.name || "").split(".").pop()?.toLowerCase();
  const types = {
    pdf: "application/pdf",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    webp: "image/webp",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    xls: "application/vnd.ms-excel",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  };
  return types[extension] || "application/octet-stream";
};

export const annualTaxObligationDocumentId = (companyId, fiscalYear) =>
  `${cleanId(companyId)}__${cleanId(fiscalYear)}`;

export const subscribeAnnualTaxObligations = ({ tenantId, onData, onError }) => {
  if (!tenantId) {
    onData?.([]);
    return () => {};
  }

  return onSnapshot(
    collection(db, "tenants", tenantId, "annualTaxObligations"),
    (snapshot) => {
      const rows = snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .sort((a, b) => Number(b.fiscalYear || 0) - Number(a.fiscalYear || 0));
      onData?.(rows);
    },
    onError
  );
};

export const subscribeAnnualTaxRules = ({ tenantId, onData, onError }) => {
  if (!tenantId) {
    onData?.({});
    return () => {};
  }

  return onSnapshot(
    collection(db, "tenants", tenantId, "annualTaxRules"),
    (snapshot) => {
      const rules = {};
      snapshot.docs.forEach((item) => {
        rules[item.id] = { id: item.id, ...item.data() };
      });
      onData?.(rules);
    },
    onError
  );
};

export const saveAnnualTaxRule = async ({ tenantId, declarationYear, rule, user }) => {
  if (!tenantId) throw new Error("Müşteri çalışma alanı belirlenemedi.");
  const normalized = normalizeAnnualTaxRule(rule);
  await setDoc(doc(db, "tenants", tenantId, "annualTaxRules", String(declarationYear)), {
    ...normalized,
    tenantId,
    declarationYear: Number(declarationYear),
    updatedBy: user?.uid || "",
    updatedAt: serverTimestamp()
  }, { merge: true });
  return normalized;
};

const ensureAnnualBoard = async ({ tenantId, user }) => {
  const boardRef = doc(db, "tenants", tenantId, "monthlyProcessBoards", BOARD_ID);
  const snapshot = await getDoc(boardRef);
  if (snapshot.exists()) return snapshot.data();

  const board = {
    tenantId,
    technoparkName: "Yıllık Mali Yükümlülükler",
    technoparkShortName: "Yıllık Yükümlülükler",
    isActive: true,
    order: 90,
    createdBy: user?.uid || "",
    createdByEmail: user?.email || "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  await setDoc(boardRef, board);
  return board;
};

const taskSteps = (kind) => {
  const titles = kind === "fund"
    ? [
        "Fon yükümlülüğü tutarını kontrol et",
        "Yatırım / fon yöntemini belirle",
        "İşlemi tamamla",
        "Dekont veya tevsik belgesini kaydet"
      ]
    : [
        "YMM ile rapor takvimini teyit et",
        "Tasdik raporu verilerini hazırla",
        "Raporu elektronik ortamda gönder",
        "Gönderim belgesini kaydet"
      ];

  return titles.map((title, index) => ({
    id: `${kind}-${index + 1}`,
    title,
    completed: false,
    isCompleted: false,
    order: index + 1
  }));
};

const taskPayload = ({ record, taskId, kind, month, dueDate, createdBy, user }) => ({
  tenantId: record.tenantId,
  boardId: BOARD_ID,
  processProjectId: BOARD_ID,
  createdBy: createdBy || user?.uid || "",
  createdByEmail: user?.email || "",
  companyId: record.companyId,
  companyName: record.companyName,
  companyShortName: record.companyShortName || record.companyName,
  title: kind === "fund"
    ? `${record.fiscalYear} %3 Fon Yükümlülüğü`
    : `${record.fiscalYear} YMM Tasdik Raporu`,
  month,
  scheduleType: "date",
  startDate: `${month}-01`,
  dueDate,
  endDate: dueDate,
  owner: "",
  autoRepeat: false,
  recurrence: { frequency: "monthly", interval: 1, createTiming: "periodEnd" },
  note: kind === "fund"
    ? `Hesaplanan yükümlülük: ${record.fundAmount} TL. Son tarih: ${record.fundDueDate}.`
    : `Kazanç istisnası için YMM tasdik raporu son tarihi: ${record.ymmDueDate}.`,
  notes: [],
  source: "annualTaxObligation",
  sourceId: record.id,
  sourceTaskId: taskId,
  priority: kind === "fund" ? "high" : "normal",
  templateId: "annual-tax-obligation",
  templateName: "Yıllık Beyan ve Yükümlülükler",
  status: "active",
  completionRate: 0,
  completedAt: null,
  deliveredAt: null,
  steps: taskSteps(kind),
  updatedAt: serverTimestamp()
});

const syncAnnualTasks = async ({ tenantId, record, user }) => {
  await ensureAnnualBoard({ tenantId, user });

  const taskCollection = collection(db, "tenants", tenantId, "monthlyProcesses");
  const currentTasksSnapshot = await getDocs(taskCollection);
  const existingTasks = new Map(
    currentTasksSnapshot.docs
      .filter((item) => item.data()?.sourceId === record.id && item.data()?.source === "annualTaxObligation")
      .map((item) => [item.id, item.data()])
  );
  const desired = new Map();
  const declarationYear = Number(record.declarationYear);

  if (record.fundRequired && record.fundStatus !== "completed") {
    const startMonth = Math.min(12, Math.max(1, Number(record.ruleSnapshot?.fundReminderStartMonth || 8)));
    for (let monthNumber = startMonth; monthNumber <= 12; monthNumber += 1) {
      const month = `${declarationYear}-${String(monthNumber).padStart(2, "0")}`;
      const taskId = `${record.id}__fund__${month}`;
      desired.set(taskId, {
        kind: "fund",
        month,
        dueDate: monthNumber === 12 ? record.fundDueDate : endOfMonthIso(declarationYear, monthNumber)
      });
    }
  }

  if (record.ymmRequired && record.ymmStatus !== "completed") {
    const month = String(record.ymmDueDate || "").slice(0, 7);
    const taskId = `${record.id}__ymm`;
    desired.set(taskId, { kind: "ymm", month, dueDate: record.ymmDueDate });
  }

  const batch = writeBatch(db);
  desired.forEach((task, taskId) => {
    const existing = existingTasks.get(taskId);
    batch.set(doc(taskCollection, taskId), {
      ...taskPayload({
        record,
        taskId,
        ...task,
        createdBy: existing?.createdBy,
        user
      }),
      ...(existing ? {} : { createdAt: serverTimestamp() })
    }, { merge: true });
  });
  existingTasks.forEach((_, taskId) => {
    if (!desired.has(taskId)) batch.delete(doc(taskCollection, taskId));
  });
  await batch.commit();
};

export const saveAnnualTaxObligation = async ({ tenantId, form, rule, user }) => {
  if (!tenantId) throw new Error("Müşteri çalışma alanı belirlenemedi.");
  if (!form.companyId || !form.companyName) throw new Error("Firma seçimi zorunludur.");
  if (!form.fiscalYear) throw new Error("Kazancın ait olduğu yıl zorunludur.");

  const id = annualTaxObligationDocumentId(form.companyId, form.fiscalYear);
  const calculation = calculateAnnualTaxObligation({ ...form, rule });
  const record = {
    ...form,
    ...calculation,
    id,
    tenantId,
    taxpayerType: form.taxpayerType || "corporate",
    companyShortName: form.companyShortName || form.companyName,
    fundStatus: calculation.fundRequired ? (form.fundStatus || "pending") : "not_applicable",
    ymmStatus: calculation.ymmRequired ? (form.ymmStatus || "pending") : "not_applicable",
    schemaVersion: 1,
    updatedBy: user?.uid || "",
    updatedByName: user?.displayName || user?.email || "",
    updatedAt: serverTimestamp()
  };

  const recordRef = doc(db, "tenants", tenantId, "annualTaxObligations", id);
  const existing = await getDoc(recordRef);
  await setDoc(recordRef, {
    ...record,
    ...(existing.exists() ? {} : {
      createdBy: user?.uid || "",
      createdAt: serverTimestamp()
    })
  }, { merge: true });

  await syncAnnualTasks({ tenantId, record, user });
  return { ...record, createdAt: existing.data()?.createdAt || null };
};

export const deleteAnnualTaxObligation = async ({ tenantId, record }) => {
  if (!tenantId || !record?.id) throw new Error("Silinecek kayıt bulunamadı.");
  const tasksSnapshot = await getDocs(collection(db, "tenants", tenantId, "monthlyProcesses"));
  const batch = writeBatch(db);
  tasksSnapshot.docs.forEach((item) => {
    if (item.data()?.source === "annualTaxObligation" && item.data()?.sourceId === record.id) {
      batch.delete(item.ref);
    }
  });
  batch.delete(doc(db, "tenants", tenantId, "annualTaxObligations", record.id));
  await batch.commit();

  const documents = [...(record.fundDocuments || []), ...(record.ymmDocuments || [])];
  await Promise.all(documents.map((item) => item.path
    ? deleteObject(ref(storage, item.path)).catch(() => null)
    : null));
};

export const uploadAnnualTaxDocuments = async ({ tenantId, record, kind, files, user }) => {
  if (!tenantId || !record?.id) throw new Error("Yıllık yükümlülük kaydı bulunamadı.");
  if (!['fund', 'ymm'].includes(kind)) throw new Error("Belge türü belirlenemedi.");
  const selectedFiles = Array.from(files || []);
  if (selectedFiles.length === 0) throw new Error("Yüklenecek belgeyi seçin.");

  selectedFiles.forEach((file) => {
    if (file.size > 15 * 1024 * 1024) throw new Error(`${file.name} 15 MB sınırını aşıyor.`);
    const type = resolveContentType(file);
    if (!/^(application\/pdf|image\/(png|jpeg|webp)|application\/vnd\.ms-excel|application\/vnd\.openxmlformats-officedocument\.(spreadsheetml\.sheet|wordprocessingml\.document))$/.test(type)) {
      throw new Error(`${file.name} desteklenen belge türlerinden biri değil.`);
    }
  });

  const uploaded = [];
  try {
    for (const [index, file] of selectedFiles.entries()) {
      const contentType = resolveContentType(file);
      const path = `annualTaxObligations/${tenantId}/${cleanId(record.companyId)}/${record.id}/${kind}/${Date.now()}-${index}-${cleanFileName(file.name)}`;
      const result = await uploadBytes(ref(storage, path), file, {
        contentType,
        customMetadata: {
          tenantId,
          companyId: cleanId(record.companyId),
          recordId: record.id,
          fiscalYear: String(record.fiscalYear),
          documentKind: kind,
          originalName: String(file.name || "Belge").slice(0, 500)
        }
      });
      uploaded.push({
        id: `${Date.now()}-${index}`,
        name: file.name,
        path,
        url: await getDownloadURL(result.ref),
        size: file.size,
        contentType,
        uploadedBy: user?.uid || "",
        uploadedByName: user?.displayName || user?.email || "",
        uploadedAt: new Date().toISOString()
      });
    }

    const recordRef = doc(db, "tenants", tenantId, "annualTaxObligations", record.id);
    const snapshot = await getDoc(recordRef);
    if (!snapshot.exists()) throw new Error("Yıllık yükümlülük kaydı artık mevcut değil.");
    const current = { id: snapshot.id, ...snapshot.data() };
    const documentsField = kind === "fund" ? "fundDocuments" : "ymmDocuments";
    const statusField = kind === "fund" ? "fundStatus" : "ymmStatus";
    const completedAtField = kind === "fund" ? "fundCompletedAt" : "ymmCompletedAt";
    const completedByField = kind === "fund" ? "fundCompletedBy" : "ymmCompletedBy";
    const completedByNameField = kind === "fund" ? "fundCompletedByName" : "ymmCompletedByName";
    const nextRecord = {
      ...current,
      [documentsField]: [...(current[documentsField] || []), ...uploaded],
      [statusField]: "completed",
      [completedAtField]: new Date().toISOString(),
      [completedByField]: user?.uid || "",
      [completedByNameField]: user?.displayName || user?.email || "",
      updatedBy: user?.uid || "",
      updatedByName: user?.displayName || user?.email || ""
    };
    await setDoc(recordRef, {
      [documentsField]: nextRecord[documentsField],
      [statusField]: "completed",
      [completedAtField]: nextRecord[completedAtField],
      [completedByField]: nextRecord[completedByField],
      [completedByNameField]: nextRecord[completedByNameField],
      updatedBy: nextRecord.updatedBy,
      updatedByName: nextRecord.updatedByName,
      updatedAt: serverTimestamp()
    }, { merge: true });
    await syncAnnualTasks({ tenantId, record: nextRecord, user });
    return uploaded;
  } catch (error) {
    await Promise.all(uploaded.map((item) => deleteObject(ref(storage, item.path)).catch(() => null)));
    throw error;
  }
};

export const deleteAnnualTaxDocument = async ({ tenantId, record, kind, documentItem, user }) => {
  if (!tenantId || !record?.id || !documentItem?.path) throw new Error("Silinecek belge bulunamadı.");
  if (!['fund', 'ymm'].includes(kind)) throw new Error("Belge türü belirlenemedi.");

  const recordRef = doc(db, "tenants", tenantId, "annualTaxObligations", record.id);
  const snapshot = await getDoc(recordRef);
  if (!snapshot.exists()) throw new Error("Yıllık yükümlülük kaydı artık mevcut değil.");
  const current = { id: snapshot.id, ...snapshot.data() };
  const documentsField = kind === "fund" ? "fundDocuments" : "ymmDocuments";
  const statusField = kind === "fund" ? "fundStatus" : "ymmStatus";
  const completedAtField = kind === "fund" ? "fundCompletedAt" : "ymmCompletedAt";
  const completedByField = kind === "fund" ? "fundCompletedBy" : "ymmCompletedBy";
  const completedByNameField = kind === "fund" ? "fundCompletedByName" : "ymmCompletedByName";
  const remainingDocuments = (current[documentsField] || []).filter((item) => item.path !== documentItem.path);
  const shouldReopen = remainingDocuments.length === 0;
  const nextRecord = {
    ...current,
    [documentsField]: remainingDocuments,
    [statusField]: shouldReopen ? "pending" : current[statusField],
    [completedAtField]: shouldReopen ? "" : current[completedAtField],
    [completedByField]: shouldReopen ? "" : current[completedByField],
    [completedByNameField]: shouldReopen ? "" : current[completedByNameField]
  };

  await deleteObject(ref(storage, documentItem.path));
  await setDoc(recordRef, {
    [documentsField]: remainingDocuments,
    [statusField]: nextRecord[statusField],
    [completedAtField]: nextRecord[completedAtField],
    [completedByField]: nextRecord[completedByField],
    [completedByNameField]: nextRecord[completedByNameField],
    updatedBy: user?.uid || "",
    updatedByName: user?.displayName || user?.email || "",
    updatedAt: serverTimestamp()
  }, { merge: true });
  await syncAnnualTasks({ tenantId, record: nextRecord, user });
};

export const ensureAnnualTaxReminders = async ({ tenantId, user }) => {
  if (!tenantId || !user?.uid) return;

  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const monthKey = today.slice(0, 7);
  const monthNumber = now.getMonth() + 1;
  const snapshot = await getDocs(collection(db, "tenants", tenantId, "annualTaxObligations"));
  const reminders = [];

  snapshot.docs.forEach((item) => {
    const record = { id: item.id, ...item.data() };
    const fundStartMonth = Number(record.ruleSnapshot?.fundReminderStartMonth || 8);
    const isFundReminderPeriod = Number(record.declarationYear) === now.getFullYear() && monthNumber >= fundStartMonth;

    if (record.fundRequired && record.fundStatus !== "completed" && isFundReminderPeriod) {
      reminders.push({
        id: `annual__${cleanId(user.uid)}__${record.id}__fund__${monthKey}`,
        title: "%3 fon yükümlülüğü hatırlatması",
        body: `${record.companyShortName || record.companyName} · ${record.fiscalYear} dönemi · ${Number(record.fundAmount || 0).toLocaleString("tr-TR")} TL · Son tarih ${record.fundDueDate}.`,
        type: record.fundDueDate < today ? "warning" : "task",
        record
      });
    }

    const ymmDueMonth = String(record.ymmDueDate || "").slice(0, 7);
    if (record.ymmRequired && record.ymmStatus !== "completed" && monthKey >= ymmDueMonth) {
      reminders.push({
        id: `annual__${cleanId(user.uid)}__${record.id}__ymm__${monthKey}`,
        title: "YMM tasdik raporu hatırlatması",
        body: `${record.companyShortName || record.companyName} · ${record.fiscalYear} dönemi · Son tarih ${record.ymmDueDate}.`,
        type: record.ymmDueDate < today ? "warning" : "task",
        record
      });
    }
  });

  await Promise.all(reminders.map(async (reminder) => {
    const notificationRef = doc(db, "systemNotifications", reminder.id);
    const existing = await getDoc(notificationRef);
    if (existing.exists()) return;
    await setDoc(notificationRef, {
      audience: "user",
      targetUserId: user.uid,
      targetUserEmail: user.email || "",
      title: reminder.title,
      body: reminder.body,
      type: reminder.type,
      link: "annualTaxObligations",
      processId: "",
      processProjectId: BOARD_ID,
      isRead: false,
      createdBy: "system",
      createdByUid: "system",
      createdAt: serverTimestamp()
    });
  }));
};
